/**
 * À la carte catalog: workflow constants, department → internal team routing,
 * and migration-on-read for Firestore `alaCarteItems` documents.
 */

import { normalizeExclusiveChoiceItemIds } from './alaCarteExclusiveChoice';

/** @typedef {'draft'|'unpromoted'|'in_review'|'awaiting_team'|'team_submitted'|'exec_ready'|'published'} PricingStatus */

export const PRICING_STATUS = {
  draft: 'draft',
  // Unpromoted: stays on file (existing client deliverables untouched) but is
  // hidden from every purchase surface — shop, plan editing, program builders.
  unpromoted: 'unpromoted',
  in_review: 'in_review',
  // Legacy team pricing workflow statuses (bids + posted items) — read as "In review".
  awaiting_team: 'awaiting_team',
  team_submitted: 'team_submitted',
  exec_ready: 'exec_ready',
  published: 'published',
};

/**
 * Catalog-facing status labels (CRM item overview + catalog list). Legacy team
 * pricing workflow values all read as "In review" — the team queue pages keep
 * their own finer-grained labels.
 */
export const CATALOG_STATUS_LABELS = {
  [PRICING_STATUS.draft]: 'Draft',
  [PRICING_STATUS.unpromoted]: 'Unpromoted',
  [PRICING_STATUS.in_review]: 'In review',
  [PRICING_STATUS.awaiting_team]: 'In review',
  [PRICING_STATUS.team_submitted]: 'In review',
  [PRICING_STATUS.exec_ready]: 'In review',
  [PRICING_STATUS.published]: 'Published',
};

/** The four statuses offered in the item overview picker. */
export const CATALOG_STATUS_OPTIONS = [
  PRICING_STATUS.draft,
  PRICING_STATUS.unpromoted,
  PRICING_STATUS.in_review,
  PRICING_STATUS.published,
];

export function catalogStatusLabel(pricingStatus) {
  return CATALOG_STATUS_LABELS[pricingStatus] || pricingStatus || 'Draft';
}

/** True for `in_review` and the legacy team pricing workflow statuses. */
export function isCatalogStatusInReview(pricingStatus) {
  return (
    pricingStatus === PRICING_STATUS.in_review ||
    pricingStatus === PRICING_STATUS.awaiting_team ||
    pricingStatus === PRICING_STATUS.team_submitted ||
    pricingStatus === PRICING_STATUS.exec_ready
  );
}

/** Maps item `department` to `internal_users.team` for posting workflow. */
export const DEPARTMENT_TO_INTERNAL_TEAM = {
  Marketing: 'Marketing',
  Sales: 'Sales',
  Operations: 'Operations',
  Finance: 'Finance',
  Technology: 'Technology',
  HR: 'HR',
  General: 'General',
};

export function departmentToPostedTeam(department) {
  return DEPARTMENT_TO_INTERNAL_TEAM[department] || 'General';
}

/**
 * Markup % vs internal cost: (client - internal) / internal * 100
 * Profit margin % vs client price: (client - internal) / client * 100
 */
export function computePricingMetrics(internalCost, clientPrice) {
  const internal = Number(internalCost);
  const client = Number(clientPrice);
  if (!Number.isFinite(internal) || !Number.isFinite(client) || internal < 0 || client < 0) {
    return { markupPercent: null, marginPercent: null };
  }
  if (internal === 0) {
    return {
      markupPercent: client > 0 ? null : 0,
      marginPercent: client > 0 ? 100 : null,
    };
  }
  const markupPercent = ((client - internal) / internal) * 100;
  const marginPercent = client > 0 ? ((client - internal) / client) * 100 : null;
  return { markupPercent, marginPercent };
}

/**
 * Case-by-case items have no catalog price — they carry our markup multiplier
 * instead, so the bid price is internal cost × multiplier and the margin on that
 * price is 1 − 1/multiplier.
 * @param {number|string} multiplier
 * @returns {number|null} null when blank or not a positive number
 */
export function marginPercentFromMultiplier(multiplier) {
  const m = Number(multiplier);
  if (!Number.isFinite(m) || m <= 0) return null;
  return (1 - 1 / m) * 100;
}

function defaultDeliverableRows(data) {
  const existing = Array.isArray(data.deliverableRows) ? data.deliverableRows : [];
  if (existing.length > 0) return existing;
  const del = data.deliverable || {};
  if (data.hasDeliverable && String(del.quantity || '').trim() !== '') {
    return [{ quantity: del.quantity, description: '' }];
  }
  return [];
}

function defaultTeamInput(data) {
  const ti = data.teamInput && typeof data.teamInput === 'object' ? data.teamInput : {};
  const rows = Array.isArray(ti.deliverableRows) ? ti.deliverableRows : [];
  return {
    scope: typeof ti.scope === 'string' ? ti.scope : '',
    internalCostEstimate:
      ti.internalCostEstimate === '' || ti.internalCostEstimate === undefined || ti.internalCostEstimate === null
        ? ''
        : ti.internalCostEstimate,
    deliverableRows: rows,
  };
}

function defaultInternalOnly(data) {
  const io = data.internalOnly && typeof data.internalOnly === 'object' ? data.internalOnly : {};
  return {
    notes: typeof io.notes === 'string' ? io.notes : '',
  };
}

function normalizeBidNegotiation(raw) {
  const o = raw && typeof raw === 'object' ? raw : {};
  return {
    counterClientPrice:
      o.counterClientPrice !== undefined && o.counterClientPrice !== null ? String(o.counterClientPrice) : '',
    counterInternalCost:
      o.counterInternalCost !== undefined && o.counterInternalCost !== null ? String(o.counterInternalCost) : '',
    counterNote: typeof o.counterNote === 'string' ? o.counterNote : '',
    execDecisionNote: typeof o.execDecisionNote === 'string' ? o.execDecisionNote : '',
  };
}

function normalizeCatalogChangeLogEntry(entry, index) {
  if (!entry || typeof entry !== 'object') return null;
  const at = entry.at ?? entry.createdAt ?? null;
  return {
    version: typeof entry.version === 'number' ? entry.version : index + 1,
    at,
    byUid: typeof entry.byUid === 'string' ? entry.byUid : '',
    changes: Array.isArray(entry.changes) ? entry.changes.map(String) : typeof entry.summary === 'string' ? [entry.summary] : [],
  };
}

function defaultCatalogChangeLog(data) {
  const raw = Array.isArray(data.catalogChangeLog) ? data.catalogChangeLog : [];
  return raw.map((e, i) => normalizeCatalogChangeLogEntry(e, i)).filter(Boolean);
}

/**
 * Normalize a Firestore `alaCarteItems` doc for CRM UI (defaults + migration).
 * @param {string} id
 * @param {Record<string, unknown>} data
 */
export function normalizeAlaCarteItem(id, data) {
  const pricingType = data.pricingType || 'packages';
  const hasDeliverable = !!data.hasDeliverable;
  const rawDel = data.deliverable && typeof data.deliverable === 'object' ? data.deliverable : {};
  const deliverable = {
    quantity: '',
    maxQuantity: '',
    period: 'One Time',
    programs: [],
    ...rawDel,
  };

  const deliverableRows = defaultDeliverableRows({
    ...data,
    hasDeliverable,
    deliverable,
  });

  return {
    ...data,
    id,
    pricingType,
    exclusiveChoiceItemIds: normalizeExclusiveChoiceItemIds(data.exclusiveChoiceItemIds, id),
    standardPricing: {
      cost: 0,
      unit: 'item',
      stripeProductId: '',
      ...(data.standardPricing || {}),
    },
    hasDeliverable,
    popularAddOn: data.popularAddOn === true,
    minimumTermMonths:
      data.minimumTermMonths === '' || data.minimumTermMonths === undefined || data.minimumTermMonths === null
        ? ''
        : data.minimumTermMonths,
    caseByCaseMultiplier:
      data.caseByCaseMultiplier === '' ||
      data.caseByCaseMultiplier === undefined ||
      data.caseByCaseMultiplier === null
        ? ''
        : data.caseByCaseMultiplier,
    deliverable,
    icon: data.icon || '📦',
    scope: typeof data.scope === 'string' ? data.scope : '',
    deliverableRows,
    internalOnly: defaultInternalOnly(data),
    teamInput: defaultTeamInput(data),
    pricingStatus: data.pricingStatus || PRICING_STATUS.draft,
    postedToTeam: data.postedToTeam ?? null,
    postedAt: data.postedAt ?? null,
    postedByUid: data.postedByUid ?? null,
    teamSubmittedAt: data.teamSubmittedAt ?? null,
    internalCost:
      data.internalCost === '' || data.internalCost === undefined || data.internalCost === null
        ? ''
        : data.internalCost,
    markupPercent:
      data.markupPercent === '' || data.markupPercent === undefined || data.markupPercent === null
        ? ''
        : data.markupPercent,
    clientListPrice:
      data.clientListPrice === '' || data.clientListPrice === undefined || data.clientListPrice === null
        ? ''
        : data.clientListPrice,
    catalogVersion: typeof data.catalogVersion === 'number' ? data.catalogVersion : 0,
    catalogChangeLog: defaultCatalogChangeLog(data),
    bidNegotiation: normalizeBidNegotiation(data.bidNegotiation),
  };
}

export function mapAlaCarteSnapshotDocs(docs) {
  return docs.map((d) => normalizeAlaCarteItem(d.id, d.data()));
}
